/**
 * Email writing formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_writing_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const emailWritingFormatsPack: ToilePack = {
    slug: 'email-writing-formats',
    category: 'classroom',
    name: 'Email writing formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'teacher', label: 'To a teacher', subject: 'an email window drawn on paper with "To", "CC", "Subject", "Salutation", "Body", "Closing", "Signature" labelled'},
        {key: 'complaint', label: 'Complaint email', subject: 'an email format with each part in a box and arrows naming them'},
        {key: 'enquiry', label: 'Enquiry email', subject: 'an email format on a ruled sheet with subject line "Enquiry about Summer Camp"'},
    ],
    styles: [
        {key: 'notebook', label: 'Notebook page', style: 'school exercise notebook page, neat hand-lettered pen labels on ruled lines, red margin line', palette: 'blue ruled lines, black pen labels, red margin line', framing: 'flat top-down page filling the frame, straight on'},
        {key: 'colorcoded', label: 'Colour-coded', style: 'clean printable teaching worksheet, bold rounded headings in boxes, each part of the format in its own colour', palette: 'navy, teal and orange labels and boxes on white paper', framing: 'flat page centered with an even white margin'},
        {key: 'mono', label: 'Black and white', style: 'photocopy-ready black-and-white worksheet, thin crisp outlines, bracket and arrow callouts naming each part', palette: 'black ink only', framing: 'flat page centered with an even white margin'},
    ],
    directions: ['red pen marks', 'filled sample', 'bigger labels'],
    directionHint: 'try: red pen marks',
    seo: {
        title: 'Email Writing Format: Teacher, Complaint, Enquiry',
        metaDescription: 'Email writing format on one labelled page: a formal email to a teacher, a complaint email and an enquiry email, in three styles.',
        h1: 'Email writing format: formal email to a teacher, complaint, enquiry',
        tagline: 'Nine labelled pages, three emails in notebook, colour-coded and black and white styles.',
        intro: 'A printable worksheet showing the layout of a formal email, with each part labelled: to, CC, subject, salutation, body, closing and signature. The nine pages show an email to a teacher, a complaint email and an enquiry email with a subject line about a summer camp. Ask for another email and the same labelled layout is asked for.',
        useCases: [
            'a classroom wall chart for email writing',
            'a reference page for a writing lesson',
            'a handout before an exam',
            'a sheet for writing to a school office',
        ],
        faq: [
            {q: 'Which parts are labelled?', a: 'To, CC, subject, salutation, body, closing and signature.'},
            {q: 'Does the page show a full email?', a: 'No. The body is drawn as ruled lines, so the page shows the layout, not the wording.'},
            {q: 'How do the three styles differ?', a: 'Notebook page is hand-lettered pen labels on ruled lines with a red margin, like a school exercise book. Colour-coded gives each part its own colour in a box. Black and white is thin outlines in ink only, ready to photocopy.'},
            {q: 'Can I ask for a different kind of email?', a: 'Yes. Add a direction for another kind of email and the labelled layout is asked for with it.'},
        ],
    },
}
